<template>
    <div class="profile-card subscription-card">
        <div class="card-header">
            <h2>Subscription</h2>
            <button
                v-if="hasActiveSubscription"
                @click="$emit('manage-subscription')"
                class="manage-btn"
                :disabled="managingSubscription"
            >
                {{ managingSubscription ? 'Loading...' : 'Manage Subscription' }}
            </button>
        </div>
        <div class="card-content">
            <div class="current-tier">
                <div class="tier-info">
                    <span class="tier-label">Current Plan</span>
                    <div class="tier-info-right">
                        <div v-if="subscriptionExpiresAt" class="tier-expiry" :class="{ 'expired': isExpired }">
                            <span class="expiry-label">{{ getExpiryLabel }}</span>
                            <span class="expiry-date">{{ formattedExpiryDate }}</span>
                        </div>
                        <div
                            class="tier-badge-wrapper"
                            @mouseenter="showCancelTooltip = true"
                            @mouseleave="showCancelTooltip = false"
                        >
                            <div
                                class="tier-badge"
                                :class="[getTierClass(subscriptionTier), { 'promo-mode': isPromoMode }]"
                                :title="isPromoMode ? 'You have Pro access during the promotional period' : ''"
                            >
                                <TriangleAlert v-if="isCanceledAtPeriodEnd" class="cancel-icon" :size="16" />
                                {{ getTierDisplayName(subscriptionTier) }}
                            </div>
                            <div v-if="isCanceledAtPeriodEnd && showCancelTooltip" class="cancel-tooltip">
                                <span class="tooltip-title">Subscription Cancelled</span>
                                <span class="tooltip-text">You'll retain access until your billing period ends on {{ formattedExpiryDate }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="tier-features">
                    <h3>Your Plan Includes:</h3>
                    <ul class="features-list">
                        <li v-for="feature in currentTierFeatures" :key="feature">
                            <span class="checkmark">✓</span> {{ feature }}
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Plan Options -->
            <div class="upgrade-section">
                <h3>{{ subscriptionTier === 'TIER 2' ? 'Manage Your Plan' : 'Upgrade Your Plan' }}</h3>

                <!-- Billing Cycle Toggle (only show for users who can upgrade) -->
                <div class="billing-toggle-wrapper">
                    <div class="billing-toggle">
                        <button
                            :class="['toggle-btn', { active: billingCycle === 'monthly' }]"
                            @click="billingCycle = 'monthly'"
                        >
                            Monthly
                        </button>
                        <button
                            :class="['toggle-btn', { active: billingCycle === 'annual' }]"
                            @click="billingCycle = 'annual'"
                        >
                            Annual
                        </button>
                    </div>
                    <span class="billing-hint">Save 17% with annual billing</span>
                </div>

                <div class="tier-options">
                    <div
                        v-for="tier in availableTiers"
                        :key="tier.name"
                        class="tier-option"
                        :class="[
                            { 'current': tier.name === subscriptionTier },
                            getTierClass(tier.name)
                        ]"
                    >
                        <div class="tier-option-header">
                            <span class="tier-option-name">{{ getTierDisplayName(tier.name) }}</span>
                            <span class="tier-option-price">
                                <span v-if="getDisplayPrice(tier).sub" class="price-sub">({{ getDisplayPrice(tier).sub }})</span>
                                {{ getDisplayPrice(tier).main }}
                            </span>
                        </div>
                        <ul class="tier-option-features">
                            <li v-for="feature in tier.features" :key="feature">
                                {{ feature }}
                            </li>
                        </ul>
                        <!-- For existing subscribers upgrading: go to Customer Portal -->
                        <button
                            v-if="tier.name !== subscriptionTier && hasActiveSubscription && tier.name !== 'FREE'"
                            @click="$emit('manage-subscription')"
                            class="tier-action-btn"
                            :class="getTierButtonClass(tier.name)"
                            :disabled="managingSubscription"
                        >
                            {{ managingSubscription ? 'Loading...' : (isUpgrade(tier.name) ? 'Upgrade' : 'Downgrade') }}
                        </button>
                        <!-- Cancel subscription button for FREE tier when user has active subscription -->
                        <button
                            v-else-if="tier.name === 'FREE' && hasActiveSubscription"
                            @click="$emit('manage-subscription')"
                            class="tier-action-btn cancel-btn"
                            :disabled="managingSubscription"
                        >
                            {{ managingSubscription ? 'Loading...' : 'Cancel Plan' }}
                        </button>
                        <!-- For new subscribers (no active subscription): go to Checkout -->
                        <button
                            v-else-if="tier.name !== subscriptionTier && !hasActiveSubscription && tier.name !== 'FREE'"
                            @click="$emit('upgrade-tier', { tier: tier.name, billingCycle })"
                            class="tier-action-btn"
                            :class="getTierButtonClass(tier.name)"
                            :disabled="upgrading"
                        >
                            {{ upgrading ? 'Redirecting to checkout...' : 'Subscribe' }}
                        </button>
                        <!-- Current plan badge -->
                        <div v-else class="current-plan-badge" :class="getTierButtonClass(tier.name)">
                            Current Plan
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref } from 'vue';
import { getTierDisplayName } from '@/config/tiers';
import { TriangleAlert } from 'lucide-vue-next';
import { useFeatureAccess } from '@/composables/useFeatureAccess';

export default {
    name: 'SubscriptionCard',
    components: {
        TriangleAlert
    },
    props: {
        subscriptionTier: {
            type: String,
            default: 'FREE'
        },
        subscriptionExpiresAt: {
            type: String,
            default: null
        },
        upgrading: {
            type: Boolean,
            default: false
        },
        getTierLevel: {
            type: Function,
            required: true
        },
        hasActiveSubscription: {
            type: Boolean,
            default: false
        },
        isCanceledAtPeriodEnd: {
            type: Boolean,
            default: false
        },
        managingSubscription: {
            type: Boolean,
            default: false
        }
    },
    emits: ['upgrade-tier', 'manage-subscription'],
    setup(props) {
        // Feature access for promo mode detection
        const { isPromoMode } = useFeatureAccess();

        // Billing cycle toggle state
        const billingCycle = ref('monthly');
        const showCancelTooltip = ref(false);

        const isExpired = computed(() => {
            if (!props.subscriptionExpiresAt) return false;
            return new Date(props.subscriptionExpiresAt) < new Date();
        });

        const formattedExpiryDate = computed(() => {
            if (!props.subscriptionExpiresAt) return '';
            const date = new Date(props.subscriptionExpiresAt);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        });

        const getExpiryLabel = computed(() => {
            if (isExpired.value) return 'Expired';
            if (props.isCanceledAtPeriodEnd) return 'Access Until';
            return 'Valid Until';
        });

        const currentTierFeatures = computed(() => {
            const features = {
                'FREE': [
                    'Access to 5 text-based questions per day',
                    'Detailed AI-powered marking of each question',
                    'Access to canvas tools'
                ],
                'TIER 1': [
                    'All FREE features',
                    'Unlimited practice questions',
                    'All question types',
                    'Access to canvas tools',
                    'Question filtering',
                    'Basic performance analytics',
                ],
                'TIER 2': [
                    'All STUDENT features',
                    'Advanced performance analytics',
                    'Question hints'
                ],
                'TIER 3': [
                    'All PRO features',
                    'Something crazy',
                ]
            };
            return features[props.subscriptionTier] || features['FREE'];
        });

        const availableTiers = computed(() => {
            return [
                {
                    name: 'FREE',
                    monthlyPrice: '$0',
                    annualPrice: '$0',
                    features: ['5 text questions/day']
                },
                {
                    name: 'TIER 1',
                    monthlyPrice: '$9.99',
                    annualPrice: '$99.90',
                    annualMonthly: '$8.33',
                    features: ['Unlimited questions and types', 'Question filtering', 'Basic analytics']
                },
                {
                    name: 'TIER 2',
                    monthlyPrice: '$13.99',
                    annualPrice: '$139.90',
                    annualMonthly: '$11.66',
                    features: ['Question hints', 'Advanced analytics']
                }
            ];
        });

        // Get display price based on billing cycle
        const getDisplayPrice = (tier) => {
            if (tier.name === 'FREE') return { main: '$0/month', sub: null };
            if (billingCycle.value === 'annual') {
                return { main: `${tier.annualPrice}/yr`, sub: `${tier.annualMonthly}/mo` };
            }
            return { main: `${tier.monthlyPrice}/mo`, sub: null };
        };

        const getTierClass = (tier) => {
            const tierLower = tier.toLowerCase().replace(/\s+/g, '-');
            return `tier-${tierLower}`;
        };

        const isUpgrade = (targetTier) => {
            const currentLevel = props.getTierLevel(props.subscriptionTier);
            const targetLevel = props.getTierLevel(targetTier);
            return targetLevel > currentLevel;
        };

        const getTierButtonClass = (tierName) => {
            const tierLower = tierName.toLowerCase().replace(/\s+/g, '-');
            return `btn-${tierLower}`;
        };

        const getTierButtonText = (tierName) => {
            if (isUpgrade(tierName)) {
                return 'Upgrade';
            } else {
                return 'Select';
            }
        };

        return {
            isExpired,
            formattedExpiryDate,
            getExpiryLabel,
            currentTierFeatures,
            availableTiers,
            billingCycle,
            showCancelTooltip,
            getTierClass,
            getTierButtonClass,
            getTierButtonText,
            getTierDisplayName,
            getDisplayPrice,
            isUpgrade,
            isPromoMode
        };
    }
};
</script>

<style scoped>
.profile-card {
    background: rgba(30, 30, 30, 0.4);
    border: 1px solid rgba(var(--accent-rgb), 0.6);
    overflow: hidden;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--accent);
}

.card-header h2 {
    margin: 0;
    color: var(--accent);
    font-size: 24px;
}

.manage-btn {
    padding: 8px 20px;
    background: transparent;
    border: 1px solid var(--accent);
    color: var(--accent);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
}

.manage-btn:hover:not(:disabled) {
    background: var(--accent);
    color: var(--bg-primary);
}

.manage-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.card-content {
    padding: 30px;
    background-color: var(--bg-primary);
}

.current-tier {
    margin-bottom: 40px;
}

.tier-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 25px;
    border-bottom: 1px solid var(--accent);
}

.tier-label {
    color: var(--accent);
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
}

.tier-info-right {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
}

.tier-expiry {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.tier-expiry.expired .expiry-label,
.tier-expiry.expired .expiry-date {
    color: #ff4444;
}

.expiry-label {
    color: var(--accent);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.expiry-date {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 500;
}

.tier-badge-wrapper {
    position: relative;
}

.tier-badge {
    padding: 8px 20px;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;
}

.tier-badge.promo-mode {
    border: 2px dashed currentColor;
    cursor: help;
}

.cancel-icon {
    color: #facc15;
    flex-shrink: 0;
}

.cancel-tooltip {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--accent);
    padding: 12px 16px;
    min-width: 240px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cancel-tooltip::before {
    content: '';
    position: absolute;
    top: -6px;
    right: 20px;
    width: 10px;
    height: 10px;
    background: var(--bg-secondary);
    border-left: 1px solid var(--accent);
    border-top: 1px solid var(--accent);
    transform: rotate(45deg);
}

.tooltip-title {
    color: #facc15;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.tooltip-text {
    color: var(--text-primary);
    font-size: 13px;
    line-height: 1.4;
    opacity: 0.9;
}

.tier-free {
    background: linear-gradient(135deg, rgba(150, 150, 150, 0.3) 0%, rgba(100, 100, 100, 0.3) 100%);
    color: #aaa;
    border: 1px solid rgba(150, 150, 150, 0.5);
}

.tier-tier-1 {
    background: linear-gradient(135deg, rgba(100, 200, 255, 0.3) 0%, rgba(50, 150, 255, 0.3) 100%);
    color: #64c8ff;
    border: 1px solid rgba(100, 200, 255, 0.5);
}

.tier-tier-2 {
    background: linear-gradient(135deg, rgba(147, 51, 234, 0.3) 0%, rgba(100, 30, 180, 0.3) 100%);
    color: #9333ea;
    border: 1px solid rgba(147, 51, 234, 0.5);
}

.tier-tier-3 {
    background: var(--bg-secondary);
    color: var(--accent);
    border: 1px solid var(--accent);
}

.tier-features h3 {
    color: var(--accent);
    font-size: 18px;
    margin-bottom: 15px;
}

.features-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.features-list li {
    color: var(--text-primary);
    padding: 8px 0;
    display: flex;
    align-items: center;
    gap: 12px;
}

.checkmark {
    color: #44ff44;
    font-weight: 600;
    font-size: 18px;
}

/* Upgrade Section */
.upgrade-section h3 {
    color: var(--accent);
    font-size: 20px;
    margin-bottom: 20px;
}

/* Billing Cycle Toggle */
.billing-toggle-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
    width: fit-content;
}

.billing-toggle {
    display: flex;
    gap: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--accent);
    border-radius: 0;
    padding: 4px;
    width: fit-content;
}

.toggle-btn {
    padding: 10px 20px;
    background: transparent;
    border: none;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
}

.toggle-btn.active {
    background: var(--accent);
    color: var(--bg-primary);
}

.toggle-btn:hover:not(.active) {
    background: rgba(var(--accent-rgb), 0.1);
}

.billing-hint {
    font-size: 12px;
    color: #22c55e;
    font-weight: 500;
}

.tier-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
}

.tier-option {
    background: var(--bg-secondary);
    border: 1px solid var(--accent);
    padding: 20px;
    transition: all 0.3s;
    display: flex;
    flex-direction: column;
}

.tier-option:hover:not(.current) {
    transform: translateY(-5px);
    border-color: var(--accent);
}

.tier-option.current {
    border-color: var(--accent);
    background: var(--bg-secondary);
}

/* Tier Option Card Themes with Backgrounds */
.tier-option.tier-free {
    border-color: rgba(150, 150, 150, 0.3);
    background: rgba(150, 150, 150, 0.05);
}

.tier-option.tier-free:hover:not(.current) {
    border-color: rgba(150, 150, 150, 0.6);
    background: rgba(150, 150, 150, 0.1);
    box-shadow: 0 8px 20px rgba(150, 150, 150, 0.2);
}

.tier-option.tier-tier-1 {
    border-color: rgba(100, 200, 255, 0.3);
    background: rgba(100, 200, 255, 0.05);
}

.tier-option.tier-tier-1:hover:not(.current) {
    border-color: #64c8ff;
    background: rgba(100, 200, 255, 0.1);
    box-shadow: 0 8px 20px rgba(100, 200, 255, 0.3);
}

.tier-option.tier-tier-2 {
    border-color: rgba(147, 51, 234, 0.3);
    background: rgba(147, 51, 234, 0.05);
}

.tier-option.tier-tier-2:hover:not(.current) {
    border-color: #9333ea;
    background: rgba(147, 51, 234, 0.1);
    box-shadow: 0 8px 20px rgba(147, 51, 234, 0.3);
}

.tier-option.tier-tier-3 {
    border-color: var(--accent);
    background: var(--bg-secondary);
}

.tier-option.tier-tier-3:hover:not(.current) {
    border-color: var(--accent);
    background: var(--bg-secondary);
}

/* Current tier gets stronger styling */
.tier-option.current.tier-free {
    border-color: rgba(150, 150, 150, 0.6);
    background: rgba(150, 150, 150, 0.15);
    box-shadow: 0 8px 20px rgba(150, 150, 150, 0.2);
}

.tier-option.current.tier-tier-1 {
    border-color: #64c8ff;
    background: rgba(100, 200, 255, 0.15);
    box-shadow: 0 8px 20px rgba(150, 150, 150, 0.2);
}

.tier-option.current.tier-tier-2 {
    border-color: #9333ea;
    background: rgba(147, 51, 234, 0.15);
    box-shadow: 0 8px 20px rgba(147, 51, 234, 0.3);
}

.tier-option.current.tier-tier-3 {
    border-color: var(--accent);
    background: var(--bg-secondary);
}

.tier-option-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 15px;
}

.tier-option.tier-free .tier-option-header {
    border-bottom: 1px solid rgba(150, 150, 150, 0.8);
}

.tier-option.tier-tier-1 .tier-option-header {
    border-bottom: 1px solid rgba(100, 200, 255, 0.8);
}

.tier-option.tier-tier-2 .tier-option-header {
    border-bottom: 1px solid rgba(147, 51, 234, 0.8);
}

.tier-option.tier-tier-3 .tier-option-header {
    border-bottom: 1px solid var(--accent);
}

.tier-option-name {
    font-weight: 600;
    font-size: 16px;
}

.tier-option.tier-free .tier-option-name {
    color: #aaa;
}

.tier-option.tier-tier-1 .tier-option-name {
    color: #64c8ff;
}

.tier-option.tier-tier-2 .tier-option-name {
    color: #9333ea;
}

.tier-option.tier-tier-3 .tier-option-name {
    color: var(--accent);
}

.tier-option-price {
    color: var(--text-primary);
    font-size: 14px;
}

.price-sub {
    opacity: 0.5;
    font-size: 12px;
}

.tier-option-features {
    list-style: none;
    padding: 0;
    margin: 0 0 20px 0;
}

.tier-option-features li {
    color: var(--text-primary);
    font-size: 13px;
    padding: 6px 0;
    opacity: 0.9;
}

.tier-option-features li:before {
    content: "•";
    color: var(--accent);
    margin-right: 8px;
}

/* Tier Action Buttons - Color matched */
.tier-action-btn {
    width: 100%;
    padding: 10px;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: 600;
    margin-top: auto;
}

.tier-action-btn:hover:not(:disabled) {
    transform: translateY(-2px);
}

.tier-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* FREE tier - Grey */
.tier-action-btn.btn-free {
    background: rgba(150, 150, 150, 0.8);
    color: #1a1a1a;
}

.tier-action-btn.btn-free:hover:not(:disabled) {
    background: #aaa;
}

/* TIER 1 - Blue */
.tier-action-btn.btn-tier-1 {
    background: #64c8ff;
    color: #1a1a1a;
}

.tier-action-btn.btn-tier-1:hover:not(:disabled) {
    background: #8ad4ff;
}

/* TIER 2 - Purple */
.tier-action-btn.btn-tier-2 {
    background: #9333ea;
    color: #fff;
}

.tier-action-btn.btn-tier-2:hover:not(:disabled) {
    background: #a855f7;
}

/* TIER 3 - Gold/Accent */
.tier-action-btn.btn-tier-3 {
    background: var(--accent);
    color: var(--bg-primary);
}

.tier-action-btn.btn-tier-3:hover:not(:disabled) {
    background: var(--text-primary);
}

/* Cancel Plan button - Red/Warning */
.tier-action-btn.cancel-btn {
    background: transparent;
    color: #ef4444;
    border: 1px solid #ef4444;
}

.tier-action-btn.cancel-btn:hover:not(:disabled) {
    background: #ef4444;
    color: #fff;
}

.current-plan-badge {
    width: 100%;
    padding: 10px;
    background: var(--bg-secondary);
    text-align: center;
    font-weight: 600;
    margin-top: auto;
}

/* Current Plan Badge - Tier Colors */
.current-plan-badge.btn-free {
    color: #aaa;
    border: 1px solid rgba(150, 150, 150, 0.5);
    background: rgba(150, 150, 150, 0.15);
}

.current-plan-badge.btn-tier-1 {
    color: #64c8ff;
    border: 1px solid rgba(100, 200, 255, 0.5);
    background: rgba(100, 200, 255, 0.15);
}

.current-plan-badge.btn-tier-2 {
    color: #9333ea;
    border: 1px solid rgba(147, 51, 234, 0.5);
    background: rgba(147, 51, 234, 0.15);
}

.current-plan-badge.btn-tier-3 {
    color: var(--accent);
    border: 1px solid var(--accent);
    background: rgba(var(--accent-rgb), 0.15);
}

@media (max-width: 768px) {
    .card-header {
        padding: 15px 20px;
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    .card-content {
        padding: 20px;
    }

    .tier-options {
        grid-template-columns: 1fr;
    }

    .tier-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }

    .tier-info-right {
        flex-direction: column;
        align-items: flex-start;
    }

    .tier-expiry {
        align-items: flex-start;
    }
}
</style>
